import { replaceDeep } from "../Layout/Router";
import React from "react";
import api from "../Auth/Api";
import globals from '../Globals'
import { historyColumns, FIXED } from "./AssetsPage";
import { Box, TextField, Button, Alert, Typography } from '@mui/material';
import { PageContainer, SelectorBar, SelectField, DataTable } from "../Layout/Common";

// One asset's history across branches and platforms (get-asset-info <platform|ALL> <branch|ALL> <name> ALL): every
// upload that changed its bytes. The Asset Manager's History dialog shows the same rows for one branch/platform.
const rank = (list, v) => (typeof v === "number" ? v : list.indexOf(v));
const SORT_KEYS = {
    branch: r => rank(globals.branches, r.branch),
    platform: r => rank(globals.platformNames, r.platform),
    version: r => r.version,
    size: r => r.size,
    seq: r => r.seq,
    createdDate: r => new Date(r.createdDate || 0).getTime(),
};
// ties: branch, platform, newest version first
const tie = (a, b) => SORT_KEYS.branch(a) - SORT_KEYS.branch(b) || SORT_KEYS.platform(a) - SORT_KEYS.platform(b) || b.version - a.version;

export default class AssetInspector extends React.Component {
    state = {
        selectedBranch: "ALL",
        selectedPlatform: "ALL",
        assetName: "",
        assetInfo: null,
        error: null,
        orderBy: "branch",
        orderDirection: "asc",
    };

    handleBranchChange = (value) => this.setState({ selectedBranch: value }, this.handleInspectClick);

    handlePlatformChange = (value) => this.setState({ selectedPlatform: value }, this.handleInspectClick);

    handleInspectClick = () => {
        const { selectedPlatform, selectedBranch } = this.state;
        const assetName = this.state.assetName.trim();
        this.updateHashWithSelectors();
        if (!assetName) return;
        api.versioning("Fetch asset info", ["get-asset-info", selectedPlatform, selectedBranch, assetName, "ALL"], { silent: true })
            .then(rows => this.setState({ assetInfo: Array.isArray(rows) ? rows : [], error: null }))
            .catch(err => this.setState({ assetInfo: null, error: err.message }));
    };

    // The selectors into the URL (no history entry): /<game>/database/inspector/<branch>/<platform>/<asset>
    updateHashWithSelectors = () => {
        const { selectedBranch, selectedPlatform, assetName } = this.state;
        replaceDeep([selectedBranch || "", selectedPlatform || "", assetName.trim()]);
    }

    componentDidMount() {
        // /<branch>/<platform>/<asset...>: an asset name may carry '/' segments
        const deep = globals.route?.deep ?? [];
        if (deep.length < 3) return;
        this.setState({ selectedBranch: deep[0], selectedPlatform: deep[1], assetName: deep.slice(2).join("/") }, this.handleInspectClick);
    }

    handleRequestSort = (property) => {
        const isAsc = this.state.orderBy === property && this.state.orderDirection === "asc";
        this.setState({ orderBy: property, orderDirection: isAsc ? "desc" : "asc" });
    };

    sortedRows() {
        const { assetInfo, orderBy, orderDirection } = this.state;
        const key = SORT_KEYS[orderBy] ?? SORT_KEYS.branch;
        const dir = orderDirection === "asc" ? 1 : -1;
        return [...(assetInfo ?? [])].sort((a, b) => {
            const x = key(a), y = key(b);
            return (x < y ? -1 : x > y ? 1 : 0) * dir || tie(a, b);
        });
    }

    render() {
        const { selectedBranch, selectedPlatform, assetName, assetInfo, error, orderBy, orderDirection } = this.state;
        return (
            <PageContainer>
                <SelectorBar>
                    <SelectField label="Branch" size="small" minWidth={160} value={selectedBranch}
                                 options={["ALL", ...globals.branches]} onChange={this.handleBranchChange} />
                    <SelectField label="Platform" size="small" minWidth={160} value={selectedPlatform}
                                 options={["ALL", ...globals.platformNames]} onChange={this.handlePlatformChange} />
                    <TextField label="Asset Name" variant="outlined" size="small" value={assetName} sx={{ minWidth: 260 }}
                               onChange={(e) => this.setState({ assetName: e.target.value })}
                               onKeyDown={(e) => { if (e.key === 'Enter') this.handleInspectClick(); }} />
                    <Button variant="contained" onClick={this.handleInspectClick} sx={{ height: 40 }}>INSPECT</Button>
                </SelectorBar>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {assetInfo && assetInfo.length === 0 && <Typography variant="body2" color="text.secondary">No history rows.</Typography>}
                {assetInfo && assetInfo.length > 0 && (
                    <Box>
                        <DataTable
                            sx={{ ...FIXED, mt: 1 }}
                            columns={historyColumns({ withPlace: true })}
                            rows={this.sortedRows()}
                            getRowKey={(r, i) => `${r.branch}/${r.platform}/${r.version}/${i}`}
                            orderBy={orderBy}
                            order={orderDirection}
                            onSort={this.handleRequestSort}
                        />
                    </Box>
                )}
            </PageContainer>
        );
    }
}
